import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiPhone, FiMapPin, FiStar, FiShoppingBag, FiUsers, FiAward } = FiIcons;

const Home = () => {
  const images = [
    'https://i.ibb.co/CpWtqssN/Gateshead-Halal-Food-Store-image-2.jpg',
    'https://i.ibb.co/RTnnNqg1/Gateshead-Halal-Food-Store-image-6.jpg',
    'https://i.ibb.co/NdyXPhfY/Gateshead-Halal-Food-Store-image-7.jpg',
    'https://i.ibb.co/sv8M3g7Y/Gateshead-Halal-Food-Store-image-8.jpg'
  ];

  const features = [
    {
      icon: FiShoppingBag,
      title: 'Fresh Halal Meat',
      description: 'Premium quality halal meat sourced from trusted suppliers'
    },
    {
      icon: FiUsers,
      title: 'Friendly Staff',
      description: 'Our experienced team is always ready to help you'
    },
    {
      icon: FiAward,
      title: 'Best Prices',
      description: 'Competitive prices without compromising on quality'
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };

  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="min-h-screen"
    >
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-secondary-600 via-primary-600 to-accent-600 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div variants={itemVariants}>
              <motion.h1 
                className="text-4xl md:text-6xl font-bold mb-6"
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
              >
                Welcome to Gateshead Halal Food Store
              </motion.h1>
              <motion.p 
                className="text-xl mb-8 text-orange-100"
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                Your trusted source for fresh halal meat, Asian groceries, and Mediterranean products in Gateshead.
              </motion.p>
              <motion.div 
                className="flex flex-col sm:flex-row gap-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
              >
                <Link
                  to="/menu"
                  className="bg-white text-primary-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors inline-flex items-center justify-center shadow-lg"
                >
                  View Our Products
                </Link>
                <a
                  href="tel:+447460008191"
                  className="border-2 border-white text-white px-8 py-3 rounded-lg font-semibold hover:bg-white hover:text-primary-600 transition-colors inline-flex items-center justify-center space-x-2"
                >
                  <SafeIcon icon={FiPhone} />
                  <span>Call Now</span>
                </a>
              </motion.div>
            </motion.div>
            <motion.div 
              variants={itemVariants}
              className="grid grid-cols-2 gap-4"
            >
              {images.slice(0, 4).map((image, index) => (
                <motion.img
                  key={index}
                  src={image}
                  alt={`Store Image ${index + 1}`}
                  className="rounded-lg shadow-lg w-full h-32 object-cover"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.6, delay: 0.8 + index * 0.1 }}
                  whileHover={{ scale: 1.05 }}
                />
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div variants={itemVariants} className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-secondary-600 via-primary-600 to-accent-600 bg-clip-text text-transparent mb-4">
              Why Choose Us?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              We've been serving the Gateshead community with quality halal products and exceptional service.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className="text-center p-6 rounded-lg hover:shadow-lg transition-shadow"
                whileHover={{ y: -5 }}
              >
                <div className="w-16 h-16 bg-gradient-to-br from-secondary-100 via-primary-100 to-accent-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={feature.icon} className="h-8 w-8 text-primary-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Quick Info Section */}
      <section className="py-16 bg-gradient-to-br from-accent-50 to-primary-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <motion.div 
              variants={itemVariants}
              className="bg-white p-8 rounded-lg shadow-lg border border-primary-200"
            >
              <h3 className="text-2xl font-bold bg-gradient-to-r from-secondary-600 to-primary-600 bg-clip-text text-transparent mb-4">Visit Our Store</h3>
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <SafeIcon icon={FiMapPin} className="text-primary-600 h-5 w-5" />
                  <span className="text-gray-700">Coastwood Road, Gateshead</span>
                </div>
                <div className="flex items-center space-x-3">
                  <SafeIcon icon={FiPhone} className="text-primary-600 h-5 w-5" />
                  <a href="tel:+447460008191" className="text-gray-700 hover:text-primary-600">
                    +447460008191
                  </a>
                </div>
              </div>
            </motion.div>
            <motion.div 
              variants={itemVariants}
              className="bg-white p-8 rounded-lg shadow-lg border border-primary-200"
            >
              <h3 className="text-2xl font-bold bg-gradient-to-r from-secondary-600 to-primary-600 bg-clip-text text-transparent mb-4">Customer Reviews</h3>
              <div className="flex items-center space-x-2 mb-2">
                <div className="flex space-x-1">
                  {[...Array(5)].map((_, i) => (
                    <SafeIcon key={i} icon={FiStar} className="h-5 w-5 text-accent-400 fill-current" />
                  ))}
                </div>
                <span className="text-gray-600">4.2/5 from 50+ reviews</span>
              </div>
              <p className="text-gray-700 italic">
                "Best halal food shop in Gateshead. Fresh products and friendly staff!"
              </p>
              <Link to="/reviews" className="text-primary-600 hover:text-primary-700 font-medium">
                Read all reviews →
              </Link>
            </motion.div>
          </div>
        </div>
      </section>
    </motion.div>
  );
};

export default Home;